import React, { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { contactApi } from "../api";
import { useTurnstile } from "./Turnstile";

const inputCls =
  "w-full px-4 py-3 text-sm bg-[#0a1826] border border-[#1d3a54] text-white placeholder-[#5b7a88] outline-none focus:border-[#29abe2] transition-colors";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const { containerRef, getToken, resetToken, configured } = useTurnstile();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please fill in your name and message.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const token = await getToken();
      if (configured && !token) {
        toast.error("Verification could not be completed. Please refresh the page and try again.");
        return;
      }
      await contactApi.submit({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        turnstile_token: token,
      });
      resetToken();
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent! We'll be in touch soon.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" style={{ backgroundColor: "#0f2438" }} className="scroll-mt-24" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 md:py-24 grid lg:grid-cols-2 gap-14">
        {/* Info */}
        <div>
          <p className="gee-kicker mb-3">Get In Touch</p>
          <h2 className="font-serif-d text-white text-3xl md:text-5xl leading-tight mb-6">
            Start your ownership journey
          </h2>
          <div className="gee-divider mb-8" />
          <p className="text-[#7d94a6] leading-relaxed max-w-md mb-10">
            Whether you're buying your first racehorse or managing an international string, we'd
            love to hear from you. Tell us about your goals and we'll be with you every step of the
            way.
          </p>
          <div className="flex flex-col gap-5 text-sm">
            <a
              href="mailto:info@globalequinegroup.com"
              className="flex items-center gap-3 text-[#b9c9d6] hover:text-[#29abe2] transition-colors w-fit"
            >
              <span className="flex items-center justify-center w-10 h-10 border border-[#1d3a54] text-[#29abe2]">
                <Mail size={16} />
              </span>
              info@globalequinegroup.com
            </a>
            <span className="flex items-center gap-3 text-[#b9c9d6]">
              <span className="flex items-center justify-center w-10 h-10 border border-[#1d3a54] text-[#29abe2]">
                <MapPin size={16} />
              </span>
              Newmarket, Suffolk, England
            </span>
          </div>
        </div>

        {/* Form */}
        <div>
          {sent ? (
            <div className="h-full flex flex-col items-center justify-center text-center border border-[#1d3a54] p-10" data-testid="contact-success">
              <span className="flex items-center justify-center w-14 h-14 rounded-full bg-[#29abe2] text-white mb-5">
                <Send size={22} />
              </span>
              <h3 className="font-serif-d text-white text-2xl mb-2">Thank you!</h3>
              <p className="text-[#7d94a6] max-w-sm">
                Your message is on its way to the team. We aim to reply within one working day.
              </p>
              <button
                onClick={() => setSent(false)}
                className="text-[#29abe2] hover:text-white text-sm mt-6 underline underline-offset-4 transition-colors"
                data-testid="contact-send-another"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="contact-form">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7d94a6] block mb-2">Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Your name"
                  className={inputCls}
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#7d94a6] block mb-2">Email *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls}
                  data-testid="contact-email-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#7d94a6] block mb-2">Subject</label>
                <input
                  value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  placeholder="e.g. Racehorse ownership enquiry"
                  className={inputCls}
                  data-testid="contact-subject-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-[#7d94a6] block mb-2">Message *</label>
                <textarea
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Tell us about your goals..."
                  rows={5}
                  className={`${inputCls} resize-none`}
                  data-testid="contact-message-input"
                />
              </div>
              <div ref={containerRef} className="sm:col-span-2" />
              <div className="sm:col-span-2 flex justify-end">
                <button type="submit" disabled={sending} className="gee-btn" data-testid="contact-submit-btn">
                  <Send size={15} /> {sending ? "Sending..." : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
